import { ConditionPredicate as Comparator } from '@pepperhq/sdk';
import { isArrayOfCurried, isDefined, isNumber, isOptional, isString, Or } from 'lib/typeguards';
import { IConditionValue } from './CampaignItem';

function isComparator<C extends Comparator>(comparator: C): (x: any) => x is C {
    return (x: any): x is C => x === comparator;
}

export interface ConditionField<C extends Comparator, Value> {
    comparator: C;
    value: Value;
}

function isConditionField<C extends Comparator, V extends string | number | any[]>(
    isC: (c: any) => c is C,
    isV: (v: any) => v is V
): (field: any) => field is ConditionField<C, V> {
    return (field: any): field is ConditionField<C, V> =>
        isDefined(field) && isC(field.comparator) && isV(field.value);
}

export enum TimeOfDay {
    EARLY_MORNING = 'EARLY_MORNING',
    MORNING = 'MORNING',
    PRE_LUNCH = 'PRE_LUNCH',
    LUNCH = 'LUNCH',
    EARLY_AFTERNOON = 'EARLY_AFTERNOON',
    AFTERNOON = 'AFTERNOON',
    EVENING = 'EVENING',
    LATE_NIGHT = 'LATE_NIGHT'
}

export function isTimeOfDay(time: any): time is TimeOfDay {
    return isString(time) && time in TimeOfDay;
}

export enum TimeConditionKey {
    timeOfDay = 'timeOfDay',
    dayOfWeek = 'dayOfWeek',
    weekOfYear = 'weekOfYear',
    monthOfYear = 'monthOfYear'
}

export interface TimeConditionFields {
    timeOfDay?: ConditionField<Comparator.IN, TimeOfDay[]>;
    dayOfWeek?: ConditionField<Comparator.EQUAL, number>;
    weekOfYear?: ConditionField<Comparator.EQUAL, number>;
    monthOfYear?: ConditionField<Comparator.EQUAL, number>;
}

export function isTimeConditionFields(fields: any): fields is TimeConditionFields {
    return (
        isDefined(fields) &&
        isOptional(
            isConditionField(isComparator(Comparator.IN), isArrayOfCurried(isTimeOfDay)),
            fields.timeOfDay
        ) &&
        isOptional(isConditionField(isComparator(Comparator.EQUAL), isNumber), fields.dayOfWeek) &&
        isOptional(isConditionField(isComparator(Comparator.EQUAL), isNumber), fields.weekOfYear) &&
        isOptional(isConditionField(isComparator(Comparator.EQUAL), isNumber), fields.monthOfYear)
    );
}

export interface LocationConditionFields {
    locationId?: ConditionField<Comparator.EQUAL, string>;
}

export function isLocationConditionFields(fields: any): fields is LocationConditionFields {
    return (
        isDefined(fields) &&
        isOptional(isConditionField(isComparator(Comparator.EQUAL), isString), fields.locationId)
    );
}

export interface MemberAddedCondition {
    type: 'MEMBER_ADDED';
    data: TimeConditionFields & {
        segmentId?: ConditionField<Comparator.IN, string[]>;
    };
}

export function isMemberAddedCondition(condition: any): condition is MemberAddedCondition {
    return (
        isDefined(condition) &&
        condition.type === 'MEMBER_ADDED' &&
        isDefined(condition.data) &&
        isOptional(
            isConditionField(isComparator(Comparator.IN), isArrayOfCurried(isString)),
            condition.data.segmentId
        ) &&
        isTimeConditionFields(condition.data)
    );
}

export interface CheckinCondition {
    type: 'CHECKIN';
    data: TimeConditionFields & LocationConditionFields;
}

export function isCheckinCondition(condition: any): condition is CheckinCondition {
    return (
        isDefined(condition) &&
        condition.type === 'CHECKIN' &&
        isDefined(condition.data) &&
        isTimeConditionFields(condition.data) &&
        isLocationConditionFields(condition.data)
    );
}

export interface OrderCompletedCondition {
    type: 'ORDER_COMPLETED';
    data: TimeConditionFields &
        LocationConditionFields & {
            amount?: ConditionField<Comparator.GREATER | Comparator.EQUAL | Comparator.LESSER, number>;
            productId?: ConditionField<Comparator.HAS_PRODUCT_ID, string>;
            categoryId?: ConditionField<Comparator.HAS_CATEGORY_ID, string>;
        };
}

export function isOrderCompletedCondition(condition: any): condition is OrderCompletedCondition {
    const isAmountComparator = Or(
        isComparator(Comparator.GREATER),
        Or(isComparator(Comparator.EQUAL), isComparator(Comparator.LESSER))
    );
    return (
        isDefined(condition) &&
        condition.type === 'ORDER_COMPLETED' &&
        isDefined(condition.data) &&
        isOptional(isConditionField(isAmountComparator, isNumber), condition.data.amount) &&
        isOptional(
            isConditionField(isComparator(Comparator.HAS_PRODUCT_ID), isString),
            condition.data.productId
        ) &&
        isOptional(
            isConditionField(isComparator(Comparator.HAS_CATEGORY_ID), isString),
            condition.data.categoryId
        ) &&
        isTimeConditionFields(condition.data) &&
        isLocationConditionFields(condition.data)
    );
}

export interface OrderUserBilledCondition {
    type: 'ORDER_USER_BILLED';
    data: TimeConditionFields &
        LocationConditionFields & {
            amount?: ConditionField<Comparator.GREATER | Comparator.EQUAL | Comparator.LESSER, number>;
            productId?: ConditionField<Comparator.HAS_PRODUCT_ID, string>;
            categoryId?: ConditionField<Comparator.HAS_CATEGORY_ID, string>;
        };
}

export function isOrderUserBilledCondition(condition: any): condition is OrderUserBilledCondition {
    const isAmountComparator = Or(
        isComparator(Comparator.GREATER),
        Or(isComparator(Comparator.EQUAL), isComparator(Comparator.LESSER))
    );

    return (
        isDefined(condition) &&
        condition.type === 'ORDER_USER_BILLED' &&
        isDefined(condition.data) &&
        isOptional(isConditionField(isAmountComparator, isNumber), condition.data.amount) &&
        isOptional(
            isConditionField(isComparator(Comparator.HAS_PRODUCT_ID), isString),
            condition.data.productId
        ) &&
        isOptional(
            isConditionField(isComparator(Comparator.HAS_CATEGORY_ID), isString),
            condition.data.categoryId
        ) &&
        isTimeConditionFields(condition.data) &&
        isLocationConditionFields(condition.data)
    );
}

export interface OrderPaymentReceivedCondition {
    type: 'ORDER_PAYMENT_RECEIVED';
    data: TimeConditionFields &
        LocationConditionFields & {
            amount?: ConditionField<Comparator.GREATER | Comparator.EQUAL | Comparator.LESSER, number>;
        };
}

export function isOrderPaymentReceivedCondition(condition: any): condition is OrderPaymentReceivedCondition {
    const isAmountComparator = Or(
        isComparator(Comparator.GREATER),
        Or(isComparator(Comparator.EQUAL), isComparator(Comparator.LESSER))
    );

    return (
        isDefined(condition) &&
        condition.type === 'ORDER_PAYMENT_RECEIVED' &&
        isDefined(condition.data) &&
        isOptional(isConditionField(isAmountComparator, isNumber), condition.data.amount) &&
        isTimeConditionFields(condition.data) &&
        isLocationConditionFields(condition.data)
    );
}

export interface RewardEarnedCondition {
    type: 'REWARD_EARNED';
    data: TimeConditionFields & LocationConditionFields;
}

export function isRewardEarnedCondition(condition: any): condition is RewardEarnedCondition {
    return (
        isDefined(condition) &&
        condition.type === 'REWARD_EARNED' &&
        isDefined(condition.data) &&
        isTimeConditionFields(condition.data) &&
        isLocationConditionFields(condition.data)
    );
}

export interface RewardBurnedCondition {
    type: 'REWARD_BURNED';
    data: TimeConditionFields & LocationConditionFields;
}

export function isRewardBurnedCondition(condition: any): condition is RewardBurnedCondition {
    return (
        isDefined(condition) &&
        condition.type === 'REWARD_BURNED' &&
        isDefined(condition.data) &&
        isTimeConditionFields(condition.data) &&
        isLocationConditionFields(condition.data)
    );
}

export interface UserActivatedCondition {
    type: 'USER_ACTIVATED';
    data: TimeConditionFields;
}

export function isUserActivatedCondition(condition: any): condition is UserActivatedCondition {
    return (
        isDefined(condition) &&
        condition.type === 'USER_ACTIVATED' &&
        isDefined(condition.data) &&
        isTimeConditionFields(condition.data)
    );
}

export interface UserCreatedCondition {
    type: 'USER_CREATED';
    data: TimeConditionFields;
}

export function isUserCreatedCondition(condition: any): condition is UserCreatedCondition {
    return (
        isDefined(condition) &&
        condition.type === 'USER_CREATED' &&
        isDefined(condition.data) &&
        isTimeConditionFields(condition.data)
    );
}

export interface VoucherRedeemedCondition {
    type: 'VOUCHER_REDEEMED';
    data: TimeConditionFields & {
        voucherId?: ConditionField<Comparator.EQUAL, string>;
    };
}

export function isVoucherRedeemedCondition(condition: any): condition is VoucherRedeemedCondition {
    return (
        isDefined(condition) &&
        condition.type === 'VOUCHER_REDEEMED' &&
        isDefined(condition.data) &&
        isOptional(isConditionField(isComparator(Comparator.EQUAL), isString), condition.data.voucherId) &&
        isTimeConditionFields(condition.data)
    );
}

export type CampaignTriggerCondition =
    | CheckinCondition
    | MemberAddedCondition
    | OrderCompletedCondition
    | OrderUserBilledCondition
    | OrderPaymentReceivedCondition
    | RewardEarnedCondition
    | RewardBurnedCondition
    | UserActivatedCondition
    | UserCreatedCondition
    | VoucherRedeemedCondition;

export function isCampaignTriggerCondition(condition: any): condition is CampaignTriggerCondition {
    return (
        isDefined(condition) &&
        (isCheckinCondition(condition) ||
            isMemberAddedCondition(condition) ||
            isOrderCompletedCondition(condition) ||
            isOrderUserBilledCondition(condition) ||
            isOrderPaymentReceivedCondition(condition) ||
            isRewardEarnedCondition(condition) ||
            isRewardBurnedCondition(condition) ||
            isUserActivatedCondition(condition) ||
            isUserCreatedCondition(condition) ||
            isVoucherRedeemedCondition(condition))
    );
}

export const campaignTriggerConditionLabel: Record<CampaignTriggerCondition['type'], string> = {
    MEMBER_ADDED: 'A user is added to an audience',
    CHECKIN: 'A user checks into a location',
    ORDER_COMPLETED: 'A user Completes an Order',
    ORDER_USER_BILLED: 'A user was billed for an order',
    ORDER_PAYMENT_RECEIVED: 'A payment was recieved for an order',
    REWARD_EARNED: 'A reward was earned by a user',
    REWARD_BURNED: 'A reward was burned by a user',
    USER_ACTIVATED: 'A user is activated',
    USER_CREATED: 'A user is created',
    VOUCHER_REDEEMED: 'A voucher is Redeemed'
};

export const timeOfDayLabel = {
    [TimeOfDay.EARLY_MORNING]: 'Early Morning',
    [TimeOfDay.MORNING]: 'Morning',
    [TimeOfDay.PRE_LUNCH]: 'Pre-Lunch',
    [TimeOfDay.LUNCH]: 'Lunch',
    [TimeOfDay.EARLY_AFTERNOON]: 'Early Afternoon',
    [TimeOfDay.AFTERNOON]: 'Afternoon',
    [TimeOfDay.EVENING]: 'Evening',
    [TimeOfDay.LATE_NIGHT]: 'Late Night'
};

export const monthsOfYear = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
];

export const timeConditionLabel = {
    [TimeConditionKey.timeOfDay]: 'At a certain time of day',
    [TimeConditionKey.dayOfWeek]: 'On a certain day of the week',
    [TimeConditionKey.weekOfYear]: 'On a certain week of the year',
    [TimeConditionKey.monthOfYear]: 'On a certain month of the year'
};

export const getConditionFieldComparator = (condition: IConditionValue) => {
    switch (condition.key) {
        case 'timeOfDay':
        case 'segmentId':
            return Comparator.IN;
        case 'categoryId':
            return Comparator.HAS_CATEGORY_ID;
        case 'productId':
            return Comparator.HAS_PRODUCT_ID;
        case 'amount':
            return condition.comparator || Comparator.EQUAL;
        default:
            return Comparator.EQUAL;
    }
};

export const getConditionFieldValue = (condition: IConditionValue) => {
    switch (condition.key) {
        case 'amount':
        case 'dayOfWeek':
        case 'weekOfYear':
        case 'monthOfYear':
            return Number(condition.value);
        default:
            return condition.value;
    }
};

export enum ConditionVariant {
    time = 'time',
    location = 'locationId',
    audience = 'segmentId',
    product = 'productId',
    category = 'categoryId',
    amount = 'amount',
    voucher = 'voucherId'
}

export const conditionTypes = [
    'CHECKIN',
    'MEMBER_ADDED',
    'ORDER_COMPLETED',
    'ORDER_PAYMENT_RECEIVED',
    'ORDER_USER_BILLED',
    'REWARD_EARNED',
    'REWARD_BURNED',
    'VOUCHER_REDEEMED',
    'USER_CREATED',
    'USER_ACTIVATED'
];

export function isConditionType(item: any): item is CampaignTriggerCondition['type'] {
    return conditionTypes.includes(item);
}

export const conditionTypeVariants: Record<string, ConditionVariant[]> = {
    CHECKIN: [ConditionVariant.location, ConditionVariant.time],
    MEMBER_ADDED: [ConditionVariant.audience, ConditionVariant.time],
    ORDER_COMPLETED: [
        ConditionVariant.location,
        ConditionVariant.product,
        ConditionVariant.category,
        ConditionVariant.amount,
        ConditionVariant.time
    ],
    ORDER_PAYMENT_RECEIVED: [ConditionVariant.amount, ConditionVariant.time],
    ORDER_USER_BILLED: [
        ConditionVariant.location,
        ConditionVariant.product,
        ConditionVariant.category,
        ConditionVariant.amount,
        ConditionVariant.time
    ],
    REWARD_EARNED: [ConditionVariant.location, ConditionVariant.time],
    REWARD_BURNED: [ConditionVariant.location, ConditionVariant.time],
    VOUCHER_REDEEMED: [ConditionVariant.voucher, ConditionVariant.time],
    USER_CREATED: [ConditionVariant.time],
    USER_ACTIVATED: [ConditionVariant.time]
};
